import React, { useState, useCallback, useMemo, useContext } from "react";
import { Container, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb/BreadCrumb";
import TableContainerMin from "../../../Components/Common/TableContainerMin";
import { useQuery, useQueryClient } from "react-query";
import { CloudUploadOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, Modal, Select, Spin, Tag } from "antd";
import HistoryContext from "../../../Components/Hooks/HistoryContext";
import toast from "react-hot-toast";
import { deleteMaterial, getMateriels, getVehicleHistory } from "../services/materielApi";
// import "../../Styles/style.css";
import AffectVehicleForm from "../component/AffectDemandeToInterv";
import { getUserById } from "../../Users/services/userApi";
import "../utils/materiels.css";
import AddEditMaterielForm from "../component/AddEditMaterielsForm";
import DetailMateriel from "../component/DetailMateriel";
import AddMaterielMass from "../component/file/AddMassMateriels";
import { formatDate } from "../../../Components/Common/FormatDate/FormatDate";
import StockSummaryCards from "../component/stocke/StockSummaryCards";

export const stateLabels = {
  NEUF: "Neuf",
  BON: "Bon",
  MAUVAIS: "Mauvais",
  HORS_SERVICE: "Hors service",
  EN_REPARATION: "En réparation",
  EN_MAINTENANCE: "En maintenance",
  PERDU: "Perdu",
  VOLE: "Volé",
  ANCIEN: "Ancien",
  EN_PANNE: "En panne",
};

const Stocke = () => {
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();
  document.title = `Stocke | ${process.env.REACT_APP_NAME} - ${process.env.REACT_APP_DESCRIPTION}`;

  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isAffectModalOpen, setIsAffectModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [driverName, setDriverName] = useState(null);
  const [selectedMateriel, setSelectedMateriel] = useState(null);
  const [selectedVehHistory, setSelectedVehHistory] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteMatName, setDeleteMatName] = useState("");
  const [bulkAddModalOpen, setBulkAddModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedState, setSelectedState] = useState("ALL");

  const { isLoading, isError, error, data: materials } = useQuery("materiels", getMateriels);
  console.log("Stocke --=>",materials)

  const { Option } = Select;

  const toggleAddEditModal = useCallback(
    (vehicule = null) => {
      setSelectedMateriel(vehicule);
      setIsAddEditModalOpen((prev) => !prev);
      if (isAffectModalOpen) setIsAffectModalOpen(false);
    },
    [isAffectModalOpen]
  );

  const handleEdit = (vehicule) => {
    toggleAddEditModal(vehicule);
  };

  const toggleBulkAddModal = () => setBulkAddModalOpen(!bulkAddModalOpen);

  const toggleHistoryModal = useCallback(
    async (vehicule = null) => {
      setSelectedMateriel(vehicule);
      try {
        const oldDriver = await getVehicleHistory(vehicule?.id);
        setSelectedVehHistory(oldDriver);
      } catch (error) {
        console.error("Error fetching material history:", error);
      }
      setIsHistoryModalOpen((prev) => !prev);
      if (isAddEditModalOpen) setIsAddEditModalOpen(false);
      if (isAffectModalOpen) setIsAffectModalOpen(false);
    },
    [isAddEditModalOpen, isAffectModalOpen]
  );

  const handleHistory = (vehicule) => {
    toggleHistoryModal(vehicule);
  };

  const toggleAffectModal = useCallback(
    (vehicule = null) => {
      setSelectedMateriel(vehicule);
      setIsAffectModalOpen((prev) => !prev);
      if (isAddEditModalOpen) setIsAddEditModalOpen(false);
    },
    [isAddEditModalOpen]
  );

  const handleAffect = async (vehicule) => {
    try {
      const selectedDriverVeh = await getUserById(vehicule?.actualUserId);
      setDriverName(selectedDriverVeh?.name || "Pas de chauffeur");
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
    toggleAffectModal(vehicule);
  };

  const handleDelete = async (matId) => {
    setIsDeleting(true);
    try {
      await deleteMaterial(matId);
      queryClient.invalidateQueries("materiels");
      toast.success("Le matériel a été supprimé avec succès");
    } catch (error) {
      console.error("Error deleting vehicule:", error);
      toast.error(`Erreur lors de la suppression du matériel`);
    } finally {
      setIsDeleting(false);
      setDeleteConfirmModal(false);
    }
  };

  const openDeleteConfirmModal = (matId) => {
    const matToDelete = materials.find((mat) => mat.id === matId);
    if (matToDelete) {
      setDeleteTargetId(matId);
      setDeleteMatName(matToDelete.name);
      setDeleteConfirmModal(true);
    }
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(false);
  };
  
  const columns = useMemo(
    () => [
      { Header: "Nom", accessor: "name", filterable: false },
      { Header: "Nombre", accessor: "nbMaterial", filterable: false },
      {
        Header: "Etat",
        accessor: "state",
        Cell: ({ value }) => (
          <Tag
            color={
              value === "NEUF"
                ? "green"
                : value === "BON"
                ? "blue"
                : value === "MAUVAIS"
                ? "orange"
                : value === "PERDU" || value === "VOLE"
                ? "red"
                : "default"
            }
          >
            {stateLabels[value] || value || "-"}
          </Tag>
        ),
      },
      { Header: "Numéro série", accessor: "serialNumber", filterable: false },
      {
        Header: "Date",
        accessor: "createdAt",
        Cell: ({ value }) => formatDate(value, true),
        filterable: false,
      },

      {
        Header: "Statut",
        accessor: "status",
        Cell: ({ value }) => (
          <span
            className={`badge bg-${
              value === "DISPONIBLE" ? "success" : "danger"
            }-subtle text-${value === "DISPONIBLE" ? "success" : "danger"}`}
          >
            {value}
          </span>
        ),
        filterable: false,
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <EyeOutlined
              title="Détail"
              size="sm"
              onClick={() => handleHistory(row.original)}
              className="btn btn-soft-primary"
            />

            <EditOutlined
              title="Modifier Materiel"
              size="sm"
              onClick={() => handleEdit(row.original)}
              className="btn btn-soft-warning"
            />

            <DeleteOutlined
              title="Supprimer matériel"
              size="sm"
              onClick={() => openDeleteConfirmModal(row.original.id)}
              className="btn btn-soft-danger"
            />
          </div>
        ),
        filterable: false,
      },
    ],
    [handleEdit, openDeleteConfirmModal, handleAffect, handleHistory]
  );

  const filteredMaterials = useMemo(() => {
    if (!materials) return [];
    if (selectedState === "ALL") return materials;

    return materials.filter((m) => m.state === selectedState);
  }, [materials, selectedState]);

  const totalStock = useMemo(() => {
    return filteredMaterials?.reduce(
      (total, item) => total + (item.nbMaterial || 0),
      0
    );
  }, [filteredMaterials]);

  const groupedStocks = useMemo(() => {
  if (!filteredMaterials?.length) return [];

  const grouped = filteredMaterials.reduce((acc, material) => {
    const key = material.name?.trim().toLowerCase();

    if (!acc[key]) {
        acc[key] = {
          name:
            material.name?.charAt(0).toUpperCase() +
            material.name?.slice(1).toLowerCase(),
          total: 0,
        };
      }

      acc[key].total += Number(material.nbMaterial || 0);

      return acc;
    }, {});

    const sorted = Object.values(grouped).sort(
      (a, b) => b.total - a.total
    );

    if (sorted.length <= 4) {
      return sorted;
    }

    const topThree = sorted.slice(0, 3);

    const othersTotal = sorted
      .slice(3)
      .reduce((sum, item) => sum + item.total, 0);

    return [
      ...topThree,
      {
        name: "Autres",
        total: othersTotal,
      },
    ];
  }, [filteredMaterials]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="Stocke" history={history} />
          <Card id="contactList">
            
            <CardHeader>
              <div className="text-title mb-4">
                Notre Stocke{" "}
                <Tag color="#5ea8adff" style={{ fontSize: "12px" }}>
                  {totalStock}
                </Tag>
              </div>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-3 w-100 mb-4">

                <div className="stock-filter">
                  <Select
                    value={selectedState}
                    onChange={setSelectedState}
                    className="stock-filter-select"
                    placeholder="Filtrer par état"
                  >
                    <Select.Option value="ALL">Tous</Select.Option>
                    {Object.entries(stateLabels).map(([key, label]) => (
                      <Select.Option key={key} value={key}>
                        {label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                <div className="d-flex flex-column flex-sm-row gap-2">
                  <Button
                    className="active-tab"
                    onClick={() => toggleBulkAddModal(null)}
                  >
                    <CloudUploadOutlined /> Ajout en masse
                  </Button>

                  <Button
                    className="active-tab"
                    onClick={() => toggleAddEditModal(null)}
                  >
                    Alimenter
                  </Button>
                </div>

              </div>
              <StockSummaryCards data={groupedStocks} />
            </CardHeader>
            <CardBody className="pt-0">
              <TableContainerMin
                columns={columns}
                data={filteredMaterials || []}
                isGlobalSearch={true}
                isGlobalFilter={true}
                customPageSize={10}
                className="custom-header-css"
                divClass="table-responsive table-card mb-3"
                tableClass="align-middle table-nowrap"
                theadClass="table-light"
              />
              {isLoading && !isError && (
                <div className="text-center">
                  Chargement, Merci de patienter ... <Spin />
                </div>
              )}
              {materials && materials?.length === 0 && !isLoading && (
                <div className="text-center">
                  Aucun matériel à afficher pour le moment.
                </div>
              )}
            </CardBody>
          </Card>

          <AddEditMaterielForm
            modal={isAddEditModalOpen}
            setModal={setIsAddEditModalOpen}
            toggle={toggleAddEditModal}
            selectedMateriel={selectedMateriel}
          />
          <AffectVehicleForm
            modal={isAffectModalOpen}
            setModal={setIsAffectModalOpen}
            toggle={toggleAffectModal}
            selectedMateriel={selectedMateriel}
            actualDriver={driverName}
          />
          <DetailMateriel
            modal={isHistoryModalOpen}
            setModal={setIsHistoryModalOpen}
            toggle={toggleHistoryModal}
            selectedMateriel={selectedMateriel}
            selectedVehHistory={selectedVehHistory}
          />
          <AddMaterielMass
            modal={bulkAddModalOpen}
            setModal={setBulkAddModalOpen}
            toggle={toggleBulkAddModal}
          />

          <Modal
            title={null}
            open={deleteConfirmModal}
            onCancel={closeDeleteConfirmModal}
            footer={null}
            centered
            className="custom-delete-modal"
          >
            <div className="text-center p-3">
              <DeleteOutlined className="mb-3" style={{ fontSize: "48px", color: "#ff4d4f" }} />

              <h4 style={{ fontWeight: "600", marginBottom: "20px" }}>
                Confirmer la suppression
              </h4>

              <p className="text-muted mt-2 mb-2">
                Êtes-vous sûr de vouloir supprimer le matériel{" "}
                <Tag color="red">{deleteMatName}</Tag> ?
              </p>

              <div className="d-flex justify-content-center gap-3 mt-4">
                <Button onClick={closeDeleteConfirmModal} size="middle">
                  Annuler
                </Button>
                <Button
                  type="primary"
                  danger
                  loading={isDeleting}
                  onClick={() => handleDelete(deleteTargetId)}
                >
                  {isDeleting ? "Suppression..." : "Oui, Supprimer !"}
                </Button>
              </div>
            </div>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Stocke;
